import { Component, type ErrorInfo, type ReactNode } from "react";
import posthog from "posthog-js";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Unconditional, app-wide React error boundary.
 *
 * The only other boundary in the app is `PostHogErrorBoundary`, which is
 * mounted *conditionally* (telemetry must be enabled). In local dev, when
 * telemetry is disabled, or in a self-host without a PostHog token, an
 * uncaught render error would otherwise unmount the entire tree to a blank
 * page with no recovery path. This boundary guarantees a recoverable fallback
 * in every configuration.
 *
 * It is intentionally mounted *outside* `TelemetryRoot`, so when telemetry is
 * enabled the nested `PostHogErrorBoundary` (closer to the throw) catches and
 * reports first; this boundary is the always-present safety net underneath.
 * The fallback deliberately avoids app context/providers that may already be
 * unmounted — it renders plain DOM and recovers via a full reload.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Always surface to the console; best-effort report to PostHog if it was
    // initialized (no-op otherwise, and never allowed to throw).
    console.error("Unhandled render error", error, info.componentStack);
    try {
      posthog.captureException?.(error, {
        component_stack: info.componentStack,
        boundary: "app-root",
      });
    } catch {
      // ignore — telemetry must never break the fallback
    }
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div
        role="alert"
        className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-wiki-bg px-6 text-center"
      >
        <div className="max-w-md space-y-3">
          <h1 className="text-lg font-semibold text-wiki-fg-1">
            Something went wrong
          </h1>
          <p className="text-sm text-wiki-fg-3">
            The app hit an unexpected error and couldn&apos;t finish rendering.
            Reloading usually fixes it. If it keeps happening, let us know.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md bg-wiki-accent px-4 py-2 text-sm font-medium text-white"
          >
            Reload
          </button>
          <button
            type="button"
            onClick={() => {
              window.location.href = "/";
            }}
            className="rounded-md border border-wiki-border px-4 py-2 text-sm font-medium text-wiki-fg-2"
          >
            Go home
          </button>
        </div>
      </div>
    );
  }
}
